import {
  ContentBlockLocation,
  TrackingElementContext,
  TrackingPageContext,
  TrackingPageTypeWithId,
  TrackingPageTypeWithoutId,
  TrackingPromotionCreative,
  TrackingPromotionDestination,
  TrackingSectionContext,
} from '@aboutyou/legacy-tracking-sdk';
import {assertUnreachable, exists} from '@aboutyou/utils';
import {
  ContentBlocksSourceType,
  Promotion,
  RecoListTrackingData,
  TopBannerRecoPromotion,
} from '../trackerComponents/TrackPromotions';
import {getSections} from './trackingProductGetter';

interface getPromotionDataResponseInterface {
  promotion: {
    id: string | number;
    name: string;
    creative: TrackingPromotionCreative;
    creativeSlot?: string;
    creativeName?: string;
    destination: TrackingPromotionDestination;
    locationId?: string;
    blockType?: ContentBlockLocation;
    highlightId?: string;
    trackHighlightIdAsPageId?: boolean;
    originClickOverride?: string;
  };
  pageType?: string;
  section: TrackingSectionContext;
  element: TrackingElementContext;
}

export const getPromotionData = ({
  promotion,
  streamPosition,
  pageType,
  recoList,
  horizontalPosition,
}: {
  promotion: Promotion;
  recoList?: RecoListTrackingData;
  streamPosition: number;
  pageType?: string;
  horizontalPosition?: number;
}): getPromotionDataResponseInterface => {
  const {
    id,
    name,
    path,
    promotionId,
    source,
    external,
    locationId,
    linkedContent,
    destinationId,
    destinationFromEndpoint,
    creativeSlot,
    creativeName,
    highlightId,
    trackHighlightIdAsPageId,
    originClickOverride,
  } = getDestinationParams(promotion);

  const destination = createPromotionDestination(
    path,
    id,
    external,
    destinationId,
    destinationFromEndpoint,
  );

  const {section} = getSections({
    pageType,
    elements: recoList?.elements,
    recoType: recoList?.recoType,
    blockTypeAndId:
      promotion.source === TrackingPromotionCreative.CustomPage
        ? locationId
        : undefined,
    headline: recoList?.headline,
    sectionIndex: recoList?.sectionIndex,
    linkedContent,
  });

  const element = {
    type: 'element-context',
    rowIndexInSection: horizontalPosition ?? streamPosition,
    columnIndexInSection: streamPosition,
    elementIndexInSection: streamPosition,
    productIndexOnPage: streamPosition,
  } as TrackingElementContext;

  const promotionReturned: getPromotionDataResponseInterface['promotion'] = {
    id: promotionId || id,
    name,
    creative: source as TrackingPromotionCreative,
    creativeSlot,
    creativeName,
    destination,
    locationId,
    highlightId,
    trackHighlightIdAsPageId,
    originClickOverride,
  };

  return {
    promotion: promotionReturned,
    pageType,
    section,
    element,
  };
};

interface DestinationParamsInterface {
  id: string;
  name: string;
  path?: string;
  promotionId?: string | number;
  source:
    | TrackingPromotionCreative
    | TopBannerRecoPromotion['source']
    | ContentBlocksSourceType
    | string;
  external?: boolean;
  locationId?: string;
  linkedContent?: string;
  destinationId?: string;
  destinationFromEndpoint?: string;
  creativeSlot?: string;
  creativeName?: string;
  highlightId?: string;
  trackHighlightIdAsPageId?: boolean;
  originClickOverride?: string;
}

// eslint-disable-next-line complexity
const getDestinationParams = (
  promotion: Promotion,
): DestinationParamsInterface => {
  switch (promotion.source) {
    case TrackingPromotionCreative.Brand:
    case TrackingPromotionCreative.BrandReco: {
      if ('promotionTracker' in promotion && promotion.promotionTracker) {
        return {
          id: `${promotion.promotionTracker?.promotionId}`,
          name: promotion.promotionTracker?.promotionName || '',
          path: promotion.promotionTracker.destination,
          promotionId: promotion.promotionTracker?.promotionId,
          locationId: promotion.promotionTracker.locationId,
          creativeName: promotion.promotionTracker.creativeName,
          creativeSlot: promotion.promotionTracker.creativeSlot,
          source: promotion.source,
        };
      }

      return {
        id: `${promotion.brand!.id}`,
        name: promotion.brand!.name,
        path: promotion.brand!.path,
        promotionId: undefined,
        source: promotion.source,
        locationId: promotion.locationId!,
      };
    }
    case TrackingPromotionCreative.outfitSliderADP:
    case TrackingPromotionCreative.outfitSliderCategory:
      return {
        id: `${promotion.promotionTracker?.promotionId}`,
        name: promotion.promotionTracker?.promotionName || '',
        path: promotion.promotionTracker.destination,
        promotionId: promotion.promotionTracker?.promotionId,
        locationId: promotion.promotionTracker.locationId,
        creativeName: promotion.promotionTracker.creativeName,
        creativeSlot: promotion.promotionTracker.creativeSlot,
        source: promotion.source,
      };
    case TrackingPromotionCreative.OutfitSliderBrand:
      return {
        id: `${promotion.promotionTracker?.promotionId}`,
        name: promotion.promotionTracker?.promotionName || '',
        path: promotion.promotionTracker.destination,
        promotionId: promotion.promotionTracker?.promotionId,
        locationId: promotion.promotionTracker.locationId,
        creativeName: promotion.promotionTracker.creativeName,
        creativeSlot: promotion.promotionTracker.creativeSlot,
        source: promotion.source,
      };
    case TrackingPromotionCreative.OutfitReco:
      return {
        id: `${promotion.outfit?.id}`,
        name: promotion.outfit?.name || '',
        path: promotion.outfit?.path,
        promotionId: undefined,
        source: promotion.source,
      };
    case TrackingPromotionCreative.StoryReco:
      return {
        id: `${promotion.story.id}`,
        name: promotion.story.headline?.default || 'storyNameUnavailable',
        creativeName: 'story',
        path: promotion.story.path,
        promotionId: promotion.story.promotionId,
        locationId: promotion.story.locationId,
        source: promotion.source,
        destinationId: `${promotion.story.id}`,
      };
    case TrackingPromotionCreative.Teaser:
    case TrackingPromotionCreative.Story: {
      if ('outfit' in promotion) {
        return {
          id: `${promotion.outfit.id}`,
          name: promotion.outfit.name,
          path: promotion.outfit.path,
          source: promotion.source,
          creativeSlot: '',
        };
      }

      if ('heroElement' in promotion) {
        return {
          id: promotion.heroElement.promotionId,
          name: promotion.heroElement.promotionName,
          path: promotion.heroElement.destination,
          source: promotion.source,
          locationId: promotion.heroElement.locationId,
          creativeName: promotion.heroElement.creativeName,
          creativeSlot: promotion.heroElement.creativeSlot,
        };
      }

      return {
        id: `${promotion.teaser.promotionTracking.linkTargetId}`,
        name:
          promotion.teaser.promotionTracking.name ||
          promotion.teaser.headline ||
          '',
        path: promotion.teaser.link,
        promotionId: promotion.teaser.promotionId,
        source: promotion.source,
      };
    }
    case TrackingPromotionCreative.Highlight:
      return {
        id: promotion.promotionId,
        name: promotion.promotionName,
        path: promotion.path,
        source: promotion.source,
        external: promotion.external,
        highlightId: promotion.highlightId.toString(),
        trackHighlightIdAsPageId: promotion.trackHighlightIdAsPageId ?? false,
      };
    case TrackingPromotionCreative.BasAdpBanner:
    case TrackingPromotionCreative.BasBasketBanner:
    case TrackingPromotionCreative.BasCategoryHeader:
    case TrackingPromotionCreative.BasCategoryTeaser:
    case TrackingPromotionCreative.BasSearch:
    case TrackingPromotionCreative.BasWishlistBanner:
    case TrackingPromotionCreative.BasAddToBasketFlyOut:
    case TrackingPromotionCreative.BasOspBanner:
    case TrackingPromotionCreative.Link:
      return {
        id: `${promotion.advertisement.id}`,
        name: promotion.advertisement.name,
        path: promotion.advertisement.path,
        source: promotion.source,
        promotionId: promotion.advertisement.promotionId,
        locationId: promotion.advertisement.locationId || promotion.source,
        creativeName: promotion.advertisement.creativeName,
      };
    case TrackingPromotionCreative.BasLogoIntegration:
      return {
        id: `${promotion.advertisement.id}`,
        name: promotion.advertisement.name,
        path: promotion.advertisement.path,
        source: promotion.source,
        promotionId: promotion.advertisement.promotionId,
        locationId: promotion.source,
        creativeName: promotion.advertisement.creativeName,
      };

    case TrackingPromotionCreative.BasFlyoutTeaser:
      return {
        id: `${promotion.advertisement.id}`,
        name: promotion.advertisement.name,
        path: promotion.advertisement.path,
        source: promotion.advertisement.creativeName || promotion.source,
        promotionId: promotion.advertisement.promotionId,
        locationId:
          promotion.advertisement.locationId ||
          promotion.advertisement.creativeName ||
          promotion.source,
      };
    case TrackingPromotionCreative.BasBrowseTopBanner:
      return {
        id: `${promotion.advertisement.id}`,
        name: promotion.advertisement.name,
        path: promotion.advertisement.path,
        source: promotion.source,
        promotionId: promotion.advertisement.promotionId,
        locationId: promotion.source,
        linkedContent: TrackingPromotionCreative.BasBrowseTopBanner,
      };
    case TrackingPromotionCreative.CustomPage:
      // Getting the ID from the URL is a temporary quick win.
      // As a followup, the ID should come from the backend.
      // TODO: use ID from BE: https://aboutyou.atlassian.net/browse/ASFE-8597
      return {
        id: promotion.id,
        name: promotion.name,
        source: promotion.contentBlockSource,
        promotionId: promotion.id,
        locationId: promotion.locationId,
        path: promotion.path.url,
        external: promotion.path.external,
        destinationId: getIdFromUrl(promotion.path.url),
      };
    case TrackingPromotionCreative.Newscard:
      return {
        id: promotion.id,
        name: promotion.name,
        path: promotion.path,
        source: promotion.source,
        promotionId: promotion.id,
        locationId: promotion.locationId,
      };
    case TrackingPromotionCreative.NotificationCenterCards: {
      const notificationCenterCardsPromotion =
        promotion.notificationCenterCards;

      return {
        id: String(notificationCenterCardsPromotion.id),
        name: notificationCenterCardsPromotion.name,
        path: notificationCenterCardsPromotion.path,
        source: notificationCenterCardsPromotion.creative,
        locationId: notificationCenterCardsPromotion.locationId,
        destinationFromEndpoint: notificationCenterCardsPromotion.destination,
        originClickOverride: notificationCenterCardsPromotion.originClick,
      };
    }
    case TrackingPromotionCreative.Banner: {
      if ('promotionTracker' in promotion) {
        return {
          locationId: promotion.promotionTracker!.locationId,
          id: String(promotion.promotionTracker!.promotionId),
          name: promotion.promotionTracker!.promotionName,
          path: promotion.ctaUrl,
          source: promotion.promotionTracker!.creativeName,
          creativeSlot: promotion.promotionTracker!.creativeSlot,
          external: promotion.external,
          destinationFromEndpoint: promotion.promotionTracker!.destination,
        };
      }

      if ('teaser' in promotion) {
        return {
          id: String(promotion.teaser.promotionTracking.id),
          name: promotion.teaser.promotionTracking.name,
          path: promotion.teaser.link.linkTarget.url,
          source: promotion.sourceOverride || promotion.source,
          destinationId: `${promotion.teaser.promotionTracking.linkTargetId}`,
          creativeSlot: '',
        };
      }

      if ('campaign' in promotion) {
        return {
          id: `${promotion.campaign.id}`,
          name: promotion.campaign.name,
          path: promotion.campaign.path,
          source: promotion.source,
          destinationId: promotion.campaign.destinationId,
          locationId: promotion.campaign.linkedContent,
          linkedContent: promotion.campaign.linkedContent,
        };
      }

      return {
        // Getting the ID from the URL is a temporary quick win.
        // As a followup, the ID should come from the backend, as well as all other tracking data.
        // TODO: use ID from BE https://aboutyou.atlassian.net/browse/ASBE-2086
        id: (promotion.ctaUrl && getIdFromUrl(promotion.ctaUrl)) ?? '',
        name: promotion.name,
        path: promotion.ctaUrl,
        source: promotion.source,
        external: promotion.external,
      };
    }
    case TrackingPromotionCreative.BasOspSubscriptionTile:
      return {
        id: promotion.promotionId,
        name: 'rokt_placement',
        source: promotion.source,
        creativeSlot: 'bas_osp_banner_rokt',
        locationId: 'bas_osp_banner',
        path: 'rokt_external_link',
        external: true,
      };
    case TrackingPromotionCreative.Sovendus:
      return {
        id: promotion.promotionId,
        name: 'sovendus_osp_placement',
        source: 'bas_osp_subscription_tile',
        creativeSlot: 'bas_osp_sovendus',
        locationId: 'bas_osp_sovendus',
        path: 'sovendus_external_link',
        external: true,
      };
    case TrackingPromotionCreative.APIPromotion:
      return {
        id: promotion.apiPromotion.promotionId,
        name: promotion.apiPromotion.promotionName,
        source: promotion.apiPromotion.creativeName,
        creativeSlot: promotion.apiPromotion.creativeSlot,
        locationId: promotion.apiPromotion.locationId,
        path:
          promotion.apiPromotion.destinationUrl ??
          promotion.apiPromotion.destination,
      };
    case TrackingPromotionCreative.GenericPromotion:
      return {
        id: `${promotion.promotionTracker.promotionId}`,
        name: promotion.promotionTracker.promotionName || '',
        path: promotion.promotionTracker.destination,
        promotionId: promotion.promotionTracker.promotionId,
        locationId: promotion.promotionTracker.locationId,
        creativeName: promotion.promotionTracker.creativeName,
        creativeSlot: promotion.promotionTracker.creativeSlot,
        source: promotion.source,
      };
    case 'monitoring':
      return {
        id: `${promotion.advertisement.id}`,
        name: promotion.advertisement.name,
        source: promotion.originalSource,
        locationId: promotion.originalSource,
      };
    case 'top_banner': {
      const genderData = (gender: 'male' | 'female' | 'kids') =>
        gender === 'male'
          ? promotion.banner.maleTopBanner
          : promotion.banner.femaleTopBanner;

      const campaignData = promotion.banner.campaign;
      return {
        id: `${campaignData?.campaignId}`,
        name: campaignData?.campaignName ?? '',
        path: genderData(promotion.gender).path,
        promotionId: undefined,
        source: 'banner' as TrackingPromotionCreative,
      };
    }
    default:
      assertUnreachable(promotion as never);
  }
};

const createPromotionDestination = (
  url?: string | null,
  resourceId?: string,
  external?: boolean,
  destinationId?: string,
  destinationFromEndpoint?: string,
): TrackingPromotionDestination => {
  if (!exists(url)) {
    return {type: 'no-destination'};
  }

  if (destinationFromEndpoint) {
    return {
      type: 'from-endpoint',
      destination: destinationFromEndpoint,
      url,
    };
  }

  if (url === 'video') {
    return {type: 'video'};
  }

  if (url.includes('live-shopping')) {
    return {
      type: 'liveshopping',
      url,
    };
  }

  const externalLink = isExternalLink(url, external);
  if (externalLink) {
    return {
      type: 'external',
      url,
    };
  }

  const [path, search = ''] = url.split('?');

  // if we already get a raw destination like story|1234, we can directly use it
  const [rawDestinationType, rawDestinationId] = path ? path.split('|') : '';

  if (rawDestinationId && rawDestinationType) {
    return {
      type: 'internal',
      page: {
        id: isNaN(+rawDestinationId) ? null : +rawDestinationId,
        type: rawDestinationType,
        path: path === '' ? '/' : path,
        search,
      } as TrackingPageContext,
    };
  }

  const potentialDestinationId = Number(destinationId ?? resourceId);

  return {
    type: 'internal',
    page: {
      id: isNaN(potentialDestinationId) ? undefined : potentialDestinationId,
      type: getPageTypeFromUrl(url),
      path: path === '' ? '/' : path,
      search,
    } as TrackingPageContext,
  };
};

const isExternalLink = (url: string, external?: boolean) => {
  if (!url) {
    return false;
  }

  return (
    external ??
    (url.startsWith('//') ||
      url.startsWith('http://') ||
      url.startsWith('https://') ||
      url.startsWith('mailto:') ||
      url.startsWith('tel:'))
  );
};

const getPageTypeFromUrl = (
  url: string,
): TrackingPageTypeWithId | TrackingPageTypeWithoutId => {
  switch (true) {
    case /\/p\/.+quick=view.*/.test(url):
      return TrackingPageTypeWithId.AdpQuickView;
    case /\/p\/.+/.test(url):
      return TrackingPageTypeWithId.ADP;
    case /\/c\/.+/.test(url):
      return TrackingPageTypeWithId.Category;
    case /\/d\/.+\/overview/.test(url):
      return TrackingPageTypeWithId.DropOverview;
    case /\/d\/.+/.test(url):
      return TrackingPageTypeWithId.DropDetails;
    case /\/cp\/.+/.test(url):
      return TrackingPageTypeWithId.CustomPage;
    case /\/s\/.+/.test(url):
      return TrackingPageTypeWithId.Story;
    case /\/about\/brand\/.+/.test(url):
    case /\/b\/shop\/.+/.test(url):
    case /brand=/.test(url):
      return TrackingPageTypeWithId.Brand;
    case /\/b\/discover\/.+/.test(url):
      return TrackingPageTypeWithId.BrandDiscover;
    case /\/o\/.+/.test(url):
      return TrackingPageTypeWithId.ODP;
    case url === '':
      return TrackingPageTypeWithoutId.Browse;
    default:
      return TrackingPageTypeWithoutId.Other;
  }
};

const getIdFromUrl = (url: string): string | undefined => {
  // returns the last number (continuous digit sequence) in a string
  const result = url.match(/\d+$/);
  return result?.[0];
};
